import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Service } from '@/types'

import Link from 'next/link'
import { ArrowRight, Check, Globe, Sparkles } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { getServiceBySlug, getServices } from '@/lib/db/queries'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { SectionHeading } from '@/components/generals/section-heading'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'
import { ServiceCard } from '@/components/landing/service-card'
import { cateringProcess } from '@/constants/process'
import { whatsappLink } from '@/constants/site'

interface ServicePageProps {
    service: Service
    otherServices: Array<Service>
}

const ServicePage: FC<ServicePageProps> = ({ service, otherServices }): ReactNode => {
    return (
        <Layout>
            <Seo title={service.title} description={service.summary} ogImage={service.image ?? undefined} />

            <PageHero
                eyebrow='Our Catering Services'
                breadcrumbs={[
                    { label: 'Home', href: '/' },
                    { label: 'Services', href: '/#services' },
                    { label: <span data-mdlfy-cms={`services:${service.id}:title`}>{service.title}</span> }
                ]}
                title={<span data-mdlfy-cms={`services:${service.id}:title`}>{service.title}</span>}
                description={(
                    <p>
                        <span data-mdlfy-cms={`services:${service.id}:summary`}>{service.summary}</span>
                    </p>
                )}
                image={service.image ? { src: service.image, alt: service.image_alt ?? service.title } : undefined}
            >
                <Link href={{ pathname: '/book', query: { service: service.slug } }} className={buttonStyles({ variant: 'primary', size: 'lg' })}>
                    Book This Service
                    <ArrowRight className='size-4 transition-transform duration-300 group-hover/button:translate-x-1' aria-hidden='true' />
                </Link>

                <a
                    href={whatsappLink(`Hello Loveth Tasty, I'd like to know more about your ${service.title} service.`)}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={buttonStyles({ variant: 'outline', size: 'lg' })}
                >
                    <WhatsappIcon className='size-4' />
                    Ask on WhatsApp
                </a>
            </PageHero>

            <section aria-labelledby='service-overview-title' className='bg-brand-white py-20 lg:py-28'>
                <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='lg:col-span-7'>
                        <p className='reveal inline-flex items-center gap-3 text-[0.72rem] font-semibold tracking-[0.3em] text-brand-ember uppercase'>
                            <span aria-hidden='true' className='h-px w-8 bg-brand-gold' />
                            Overview
                        </p>

                        <h2 id='service-overview-title' className='sr-only'>
                            About this service
                        </h2>

                        {service.intro && (
                            <p className='reveal mt-6 font-heading text-[1.75rem] leading-[1.35] text-brand-charcoal sm:text-[2.1rem]'>
                                <span data-mdlfy-cms={`services:${service.id}:intro`}>{service.intro}</span>
                            </p>
                        )}

                        {service.highlights.length > 0 && (
                            <div className='reveal mt-12'>
                                <h3 className='text-xs font-semibold tracking-[0.25em] text-brand-stone uppercase'>
                                    What is included
                                </h3>

                                <ul className='mt-6 grid gap-4 sm:grid-cols-2'>
                                    {service.highlights.map(highlight => (
                                        <li key={highlight} className='flex items-start gap-3 rounded-2xl border border-brand-sand bg-brand-cream/50 p-4'>
                                            <span className='mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-orange text-brand-charcoal'>
                                                <Check className='size-3.5' aria-hidden='true' />
                                            </span>

                                            <span className='text-[15px] leading-relaxed text-brand-charcoal'>
                                                {highlight}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <aside className='reveal lg:col-span-5'>
                        <div className='sticky top-28 space-y-5'>
                            {service.occasions.length > 0 && (
                                <div className='rounded-[1.75rem] bg-brand-charcoal p-7 text-brand-white'>
                                    <h3 className='flex items-center gap-2 font-heading text-2xl font-semibold'>
                                        <Sparkles className='size-5 text-brand-gold' aria-hidden='true' />
                                        Perfect for
                                    </h3>

                                    <ul className='mt-5 flex flex-wrap gap-2'>
                                        {service.occasions.map(occasion => (
                                            <li key={occasion} className='rounded-full border border-white/15 px-3.5 py-1.5 text-sm text-brand-mist'>
                                                {occasion}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className='rounded-[1.75rem] border border-brand-sand bg-brand-cream p-7'>
                                <h3 className='font-heading text-2xl font-semibold text-brand-charcoal'>
                                    Ready to plan your menu?
                                </h3>

                                <p className='mt-2 text-[15px] leading-relaxed text-brand-stone'>
                                    Share your date and guest numbers and we will send a tailored menu and quote.
                                </p>

                                <div className='mt-5 flex flex-col gap-3'>
                                    <Link href={{ pathname: '/book', query: { service: service.slug } }} className={buttonStyles({ variant: 'primary', className: 'w-full' })}>
                                        Request a Quote
                                    </Link>

                                    <Link href='/order-from-abroad' className={buttonStyles({ variant: 'outline', className: 'w-full' })}>
                                        <Globe className='size-4' aria-hidden='true' />
                                        Ordering from abroad?
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            <section aria-labelledby='service-process-title' className='bg-brand-cream py-20 lg:py-28'>
                <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-10'>
                    <SectionHeading
                        eyebrow='How It Works'
                        titleId='service-process-title'
                        title={(
                            <>
                                Four easy steps to{' '}
                                <em className='text-brand-burnt'>a delicious event</em>
                            </>
                        )}
                    />

                    <ol className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                        {cateringProcess.map((step, index) => (
                            <li key={step.title} className='reveal rounded-[1.5rem] bg-brand-white p-7'>
                                <span aria-hidden='true' className='font-heading text-5xl leading-none font-semibold text-brand-gold'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='mt-5 text-lg font-semibold text-brand-charcoal'>
                                    {step.title}
                                </h3>

                                <p className='mt-2 text-[15px] leading-relaxed text-brand-stone'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {otherServices.length > 0 && (
                <section aria-labelledby='other-services-title' className='bg-brand-white py-20 lg:py-28'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-10'>
                        <SectionHeading
                            eyebrow='Explore More'
                            titleId='other-services-title'
                            title={(
                                <>
                                    More ways we can{' '}
                                    <em className='text-brand-burnt'>serve you</em>
                                </>
                            )}
                        />

                        <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {otherServices.map((other, index) => (
                                <ServiceCard key={other.id} service={other} index={index} />
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const services = await getServices()

        return {
            paths: services.map(service => ({ params: { slug: service.slug } })),
            fallback: 'blocking'
        }
    }
    catch (error) {
        console.error('Service pages could not be listed:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ServicePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 60 }

    try {
        const [service, services] = await Promise.all([getServiceBySlug(slug), getServices()])

        if (!service) return { notFound: true, revalidate: 60 }

        const otherServices = services.filter(other => other.id !== service.id).slice(0, 3)

        return {
            props: { service, otherServices },
            revalidate: 60
        }
    }
    catch (error) {
        console.error(`Service page "${slug}" could not be loaded:`, error instanceof Error ? error.message : error)

        throw error
    }
}

export default ServicePage
